import { useState, useRef, useEffect, useCallback, useMemo } from "preact/hooks";
import emitter from '@webcore/core/events/eventemitter';
import { IS_EYEJACK_APP } from '@webcore/core/utils/utils';
import { openURL } from '@webcore/core/openurl/openurl';
import { Fade } from '@core/components/Transitions/Fade';
import { AppClipExitScreen } from '@core/components/Layout';
import { useSplashPreloader } from '@core/hooks/useSplashPreloader';
import { useXRSession } from '@core/hooks/useXRSession';
import { useEJXLaunchUrl } from '@core/hooks/useEJXLaunchUrl';
import { useUrlContentId } from '@core/hooks/useUrlContentId';
import getUserDevice from '@core/utils/getUserDevice';
import { useXRAnalyticsSession } from './hooks/useXRAnalyticsSession';
import { useArSessionTracking } from './hooks/useArSessionTracking';
import { useXRCheckInline } from '@core/hooks/useXRCheckInline';

import { LeatherbackSection } from "./components/TurtleSections/LeatherbackSection";
import { GreenTurtleSection } from "./components/TurtleSections/GreenTurtleSection";
import { LoggerheadSection } from "./components/TurtleSections/LoggerheadSection";
import { FlatbackSection } from "./components/TurtleSections/FlatbackSection";
import { HawksbillSection } from "./components/TurtleSections/HawksbillSection";
import { OliveRidleySection } from "./components/TurtleSections/OliveRidleySection";
import { Splash } from './components/Splash';
import { VideoRecorderUI } from '@core/components/Media';
import {
  Header,
  HeroSection,
  IntroSection,
  PartnershipsSection,
  SponsorSection,
  InvestingSection,
  SupportersSection,
  TurtlesOverviewSection,
  InsideTheCentreSection,
  SavingTurtlesSection,
  KnowledgeSection,
  ButchullaSection,
  ButchullaLoresSection,
  ButchullaCountrySection,
  CultureCommunitySection,
  DaymanPointSection,
  Footer,
} from './components/PageSections';

// Content data - single source of truth
import { ContentData, findModelByIdOrHash, getModelHash, ModelData } from './data/data';
import { StartingAROverlay } from "./components/StartingAROverlay";
import { AROverlay } from "./components";

// ============================================================================
// APP STATE
// ============================================================================
enum AppState {
  LANDING = 'LANDING',
  SPLASH = 'SPLASH',
  TRANSITIONING_XR = 'TRANSITIONING_XR',
  AR_EXPERIENCE = 'AR_EXPERIENCE',
  APP_CLIP_EXITED = 'APP_CLIP_EXITED',
}

export function App() {
  const { device } = getUserDevice();
  const OVERLAY_FADE_OUT_MS = 400;
  const initialShowScrollOverlay =
    !IS_EYEJACK_APP &&
    typeof window !== 'undefined' &&
    (
      new URLSearchParams(window.location.search).get('fromAr') === '1' ||
      window.location.hash.length > 0
    );
  const [appState, setAppState] = useState<AppState>(
    IS_EYEJACK_APP ? AppState.SPLASH : AppState.LANDING
  );
  const [selectedModel, setSelectedModel] = useState<ModelData | null>(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [isPlaced, setIsPlaced] = useState(false);
  const [showDelayedAndroidLoader, setShowDelayedAndroidLoader] = useState(false);
  const [showScrollOverlay, setShowScrollOverlay] = useState(initialShowScrollOverlay);
  const [isOverlayMounted, setIsOverlayMounted] = useState(initialShowScrollOverlay);
  const [isOverlayVisible, setIsOverlayVisible] = useState(initialShowScrollOverlay);
  
  const fromLandingRef = useRef(!IS_EYEJACK_APP);
  // Exact URL to open on App Clip exit (same as Safari tab) so iOS can reuse that tab
  const safariReturnUrlRef = useRef<string | null>(null);

  const xrSession = useXRSession({
    isLoaded: contentLoaded,
    isInAppClip: IS_EYEJACK_APP,
    autoStartInAppClip: true,
  });

  const { loadingState } = useSplashPreloader({
    minDisplayTime: 2000,
    waitForFonts: true,
  });

  // XR Analytics - tracks user across Safari → App Clip with same userId
  const { initAnalytics, sendEvent, isUserIdLoading } = useXRAnalyticsSession({
    measurementId: 'G-CT6TV56P98',
    debug: import.meta.env.DEV,
  });

  // Get launch URL from EyeJack App Clip (via postMessage), falls back to window.location.href
  const launchUrl = useEJXLaunchUrl();

  // Store exact URL for openURL on exit: prefer returnUrl (Safari tab URL we passed when opening App Clip) over launchUrl
  useEffect(() => {
    if (!IS_EYEJACK_APP) return;
    const returnUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('returnUrl') : null;
    if (returnUrl && returnUrl.startsWith('http')) {
      safariReturnUrlRef.current = returnUrl;
    } else if (launchUrl && launchUrl.startsWith('http')) {
      safariReturnUrlRef.current = launchUrl;
    }
  }, [launchUrl]);

  // Parse content ID/hash from URL (supports /green, ?content=green, #green-turtle)
  const validContentIds = useMemo(
    () => ContentData.flatMap((c) => c.urlHash ? [c.id, c.urlHash] : [c.id]),
    []
  );
  const contentId = useUrlContentId({
    url: launchUrl,
    validIds: validContentIds,
    defaultId: validContentIds[0],
  });

  const isLoading = loadingState === 'loading' || !contentLoaded;

  // XR compatibility check - run once at app level, pass down to components
  const { 
    isChecking: xrIsChecking, 
    isCompatible: xrIsCompatible,
    showAppClipButton: xrShowAppClipButton,
  } = useXRCheckInline();

  // Initialize analytics when userId is ready
  // (In App Clip, userId loads async from native storage)
  useEffect(() => {
    if (isUserIdLoading) return; // Wait for userId to be ready
    if (import.meta.env.DEV) return;
    initAnalytics().then(() => {
      // Only send page_view on website, not in App Clip
      // App Clip is a continuation of the same session - ar_session_start tracks the AR launch
      if (!IS_EYEJACK_APP) {
        sendEvent('page_view', {
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }
    }).catch(() => {
      // Analytics failed to load - non-critical, continue
      console.warn('[App] Analytics initialization failed');
    });
  }, [isUserIdLoading]); // Re-run when userId becomes ready

  // Listen to web-core analytics events and forward to GA4
  useEffect(() => {
    const handleAnalyticsEvent = (event: { name: string; params?: Record<string, any> }) => {
      sendEvent(event.name, event.params);
    };
    
    // Track XR errors
    const handleXrErrorUndefined = () => {
      sendEvent('ar_error', { error_type: 'undefined' });
    };
    const handleXrErrorNotSupported = () => {
      sendEvent('ar_error', { error_type: 'not_supported' });
    };
    const handleXrErrorPermissionDenied = (data: { type?: string }) => {
      sendEvent('ar_error', { error_type: 'permission_denied', permission_type: data?.type });
    };
    
    emitter.on('analytics', handleAnalyticsEvent);
    emitter.on('xr-error-undefined', handleXrErrorUndefined);
    emitter.on('xr-error-not-supported', handleXrErrorNotSupported);
    emitter.on('xr-error-permission-denied', handleXrErrorPermissionDenied);
    
    return () => {
      emitter.off('analytics', handleAnalyticsEvent);
      emitter.off('xr-error-undefined', handleXrErrorUndefined);
      emitter.off('xr-error-not-supported', handleXrErrorNotSupported);
      emitter.off('xr-error-permission-denied', handleXrErrorPermissionDenied);
    };
  }, [sendEvent]); // Event listeners only need sendEvent

  // Track AR session duration with visibility awareness
  useArSessionTracking({
    isActive: appState === AppState.AR_EXPERIENCE && modelReady,
    hasExited: xrSession.hasExited,
    contentId: selectedModel?.id || null,
    contentName: selectedModel?.displayName || null,
    sendEvent,
  });

  // Set selected model from URL (runs once)
  // On web: lazy loading handles model load when previews scroll into view
  // On iOS App Clip: load immediately since there's no preview page
  useEffect(() => {
    if (selectedModel) return;
    
    const model = (contentId ? findModelByIdOrHash(contentId) : undefined) || ContentData[0];
    if (model) {
      setSelectedModel(model);
      
      // On iOS App Clip, load the model immediately (no preview page)
      if (IS_EYEJACK_APP) {
        (emitter as any).emit('set-model-id', model.id);
      }
    }
  }, [contentId, selectedModel]);

  // Transition to AR_EXPERIENCE when XR starts
  useEffect(() => {
    if (xrSession.isRunning && (appState === AppState.TRANSITIONING_XR || (IS_EYEJACK_APP && appState === AppState.SPLASH))) {
      document.body.classList.add('ar-mode');
      setAppState(AppState.AR_EXPERIENCE);
    }
  }, [xrSession.isRunning, appState]);

  // Start XR when content loads while in transitioning state (Android flow)
  useEffect(() => {
    if (contentLoaded && appState === AppState.TRANSITIONING_XR && !xrSession.isRunning && !xrSession.isStarting) {
      xrSession.start();
    }
  }, [contentLoaded, appState, xrSession]);

  useEffect(() => {
    if (xrSession.hasExited && appState === AppState.AR_EXPERIENCE) {
      document.body.classList.remove('ar-mode');
      
      if (IS_EYEJACK_APP) {
        setAppState(AppState.APP_CLIP_EXITED);
        const urlToOpen = safariReturnUrlRef.current || launchUrl;
        console.log('[App Clip exit] openURL:', urlToOpen, '| launchUrl:', launchUrl, '| stored:', safariReturnUrlRef.current);
        openURL(urlToOpen).catch(() => {});
      } else if (fromLandingRef.current) {
        setShowScrollOverlay(true);
        setAppState(AppState.LANDING);
        // Stay on current hash (e.g. #green-turtle); preserve query params
        window.history.pushState({}, '', `/${window.location.search}${window.location.hash}`);
      } else {
        setAppState(AppState.SPLASH);
      }
      setSelectedModel(null);
      setModelReady(false); // Reset for next AR session
      setIsPlaced(false);
    }
  }, [xrSession.hasExited, appState, launchUrl]);

  useEffect(() => {
    const handleLoaded = () => {
      setContentLoaded(true);
    };

    const handleContentVisualReady = (contentId?: string) => {
      if (contentId && contentId !== selectedModel?.id) {
        return;
      }
      // AR visuals are ready to be shown
      setModelReady(true);
    };

    const handleContentLoading = () => {
      // New content is loading - reset ready state
      setModelReady(false);
    };

    const handleContentReady = () => {
      // Model has been placed
      setIsPlaced(true);
    };

    const handleRepositioning = () => {
      // Model is being repositioned - hide recorder
      setIsPlaced(false);
    };

    emitter.on('loaded', handleLoaded);
    emitter.on('content-visual-ready', handleContentVisualReady);
    emitter.on('content-loading', handleContentLoading);
    emitter.on('content-ready', handleContentReady);
    (emitter as any).on('content-repositioning', handleRepositioning);
    
    import('./main.app').then(({ app }) => {
      if (app?.isReady()) {
        setContentLoaded(true);
      }
    });

    return () => {
      emitter.off('loaded', handleLoaded);
      emitter.off('content-visual-ready', handleContentVisualReady);
      emitter.off('content-loading', handleContentLoading);
      emitter.off('content-ready', handleContentReady);
      (emitter as any).off('content-repositioning', handleRepositioning);
    };
  }, [selectedModel?.id]);

  // ============================================================================
  // AR HANDLERS (WebXR via Babylon.js)
  // ============================================================================
  
  // Request camera permissions (for Android flow)
  const requestCameraPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately - we just needed the permission
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('Camera permission denied:', error);
      return false;
    }
  }, []);

  const trackLaunchClick = useCallback(
    (model: ModelData, options?: { transportType?: 'beacon' }) => {
      sendEvent('ar_launch_click', {
        content_id: model.id,
        content_name: model.displayName,
        ...(options?.transportType ? { transport_type: options.transportType } : {}),
      });
    },
    [sendEvent]
  );

  const handleLaunchAR = useCallback(async (model: ModelData) => {
    console.log('[View AR] launchUrl:', launchUrl, '| window.location.href:', window.location.href);
    trackLaunchClick(model);
    
    setSelectedModel(model);
    fromLandingRef.current = true;
    
    // Update URL to #urlHash + fromAr=1 so on return we scroll instantly
    const params = new URLSearchParams(window.location.search);
    params.set('fromAr', '1');
    window.history.pushState({}, '', `/?${params.toString()}#${getModelHash(model)}`);
    
    (emitter as any).emit('set-model-id', model.id);

    // iOS App Clip: use the splash/onboarding flow (handled by autoStart)
    if (IS_EYEJACK_APP) {
      setAppState(AppState.SPLASH);
      return;
    }

    // Android/Web: Request camera permission first, then go straight to AR
    const hasPermission = await requestCameraPermission();
    
    if (!hasPermission) {
      // Permission denied - don't proceed
      setSelectedModel(null);
      // Stay on current hash; preserve query params
      window.history.pushState({}, '', `/${window.location.search}${window.location.hash}`);
      return;
    }

    // Permission granted - show loading screen
    document.body.classList.add('ar-mode');
    setAppState(AppState.TRANSITIONING_XR);
    setModelReady(false); // Reset - wait for model to be ready in AR context
    setIsPlaced(false); // Reset placement state
    
    // Wait for content to be loaded before starting XR
    if (contentLoaded) {
      xrSession.start();
    }
    // If not loaded yet, the useEffect watching contentLoaded will handle starting XR
  }, [xrSession, requestCameraPermission, contentLoaded, trackLaunchClick, launchUrl]);

  const handleSplashClose = useCallback(() => {
    document.body.classList.add('ar-mode');
    setAppState(AppState.TRANSITIONING_XR);
    setModelReady(false); // Reset - wait for model to be ready in AR context
    setIsPlaced(false); // Reset placement state
    xrSession.start();
  }, [xrSession]);

  const handleExitAR = useCallback(() => {
    xrSession.stop();
  }, [xrSession]);

  const handleBackToLanding = useCallback(() => {
    document.body.classList.remove('ar-mode');
    setAppState(AppState.LANDING);
    setSelectedModel(null);
    // Stay on current hash (e.g. #green-turtle); preserve query params
    window.history.pushState({}, '', `/${window.location.search}${window.location.hash}`);
  }, []);

  const showLanding = appState === AppState.LANDING;
  const shouldShowAndroidLoadingOverlay =
    device === 'Android' &&
    appState === AppState.AR_EXPERIENCE &&
    !modelReady;

  useEffect(() => {
    if (!shouldShowAndroidLoadingOverlay) {
      setShowDelayedAndroidLoader(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowDelayedAndroidLoader(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldShowAndroidLoadingOverlay]);

  const showStartingArOverlay =
    device === 'Android'
      ? showDelayedAndroidLoader
      : (
          appState === AppState.TRANSITIONING_XR ||
          (appState === AppState.AR_EXPERIENCE && !modelReady)
        );
  const shouldShowOverlay = showStartingArOverlay || showScrollOverlay;

  useEffect(() => {
    if (shouldShowOverlay) {
      setIsOverlayMounted(true);
      setIsOverlayVisible(true);
      return;
    }

    setIsOverlayVisible(false);
    const timeoutId = window.setTimeout(() => {
      setIsOverlayMounted(false);
    }, OVERLAY_FADE_OUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldShowOverlay]);

  // Hash in URL: keep the startup overlay visible, wait for layout-critical
  // content above the target to settle, then do a final scroll.
  useEffect(() => {
    if (!showLanding) return;
    const hash = window.location.hash;
    if (!hash) return;
    const fromAr = new URLSearchParams(window.location.search).get('fromAr');
    const el = document.querySelector(hash);
    if (!el) {
      if (showScrollOverlay) setShowScrollOverlay(false);
      return;
    }

    const target = el as HTMLElement;
    let cancelled = false;

    const getHeaderHeight = () => {
      const header = document.querySelector('[data-fixed-header="true"]') as HTMLElement | null;
      return header?.offsetHeight ?? 0;
    };

    const scrollToTarget = () => {
      const top = window.scrollY + target.getBoundingClientRect().top - getHeaderHeight();
      window.scrollTo({ top, behavior: 'auto' });
    };

    const cleanUrl = () => {
      const u = new URL(window.location.href);
      u.hash = '';
      u.searchParams.delete('fromAr');
      window.history.replaceState({}, '', u.pathname + u.search);
    };

    const waitForNextFrame = () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

    const waitForFonts = async () => {
      const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts;
      if (!fonts) return;
      await Promise.race([
        fonts.ready.then(() => undefined),
        new Promise<void>((resolve) => window.setTimeout(resolve, 3000)),
      ]);
    };

    const collectBlockingImages = () => {
      return Array.from(document.images).filter((img) => {
        if (!img.src) return false;
        if (target.contains(img)) return true;
        const relation = img.compareDocumentPosition(target);
        return Boolean(relation & Node.DOCUMENT_POSITION_FOLLOWING);
      });
    };

    const primeImages = (images: HTMLImageElement[]) => {
      for (const img of images) {
        if (img.complete) continue;
        img.loading = 'eager';
        img.decoding = 'sync';
      }
    };

    const waitForImage = async (img: HTMLImageElement) => {
      if (img.complete) {
        if (typeof img.decode === 'function') {
          await img.decode().catch(() => undefined);
        }
        return;
      }

      await new Promise<void>((resolve) => {
        const finish = () => {
          window.clearTimeout(timeoutId);
          resolve();
        };
        const timeoutId = window.setTimeout(finish, 8000);

        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
      });

      if (typeof img.decode === 'function') {
        await img.decode().catch(() => undefined);
      }
    };

    const settleAndScroll = async () => {
      document.documentElement.classList.add('instant-scroll');

      const blockingImages = collectBlockingImages();
      primeImages(blockingImages);

      await waitForNextFrame();
      await waitForFonts();
      await Promise.race([
        Promise.allSettled(blockingImages.map(waitForImage)).then(() => undefined),
        new Promise<void>((resolve) => window.setTimeout(resolve, fromAr ? 12000 : 10000)),
      ]);

      if (cancelled) return;

      await waitForNextFrame();
      await waitForNextFrame();
      scrollToTarget();
      await waitForNextFrame();
      scrollToTarget();

      document.documentElement.classList.remove('instant-scroll');
      if (showScrollOverlay) {
        setShowScrollOverlay(false);
      }
      cleanUrl();
    };

    settleAndScroll();

    return () => {
      cancelled = true;
      document.documentElement.classList.remove('instant-scroll');
    };
  }, [showLanding, showScrollOverlay]);

  return (
    <>
      {showLanding && (
        <div className="relative w-full min-h-screen overflow-x-hidden bg-white">
          {/* Header */}
          <Header />

          {/* Hero Section */}
          <HeroSection />

          {/* Intro Section */}
          <IntroSection />

          {/* Partnerships */}
          <PartnershipsSection />

          {/* Investing */}
          <InvestingSection />

          {/* Supporters */}
          <SupportersSection />

          {/* Sponsor */}
          <SponsorSection />

          {/* Turtles Overview */}
          <TurtlesOverviewSection />

          {/* Turtle Sections - Explicitly rendered for per-section custom content/styling */}
          <div className="relative">
            <LeatherbackSection
              model={ContentData.find(m => m.id === "leatherback")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />

            <GreenTurtleSection
              model={ContentData.find(m => m.id === "green")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />

            <LoggerheadSection
              model={ContentData.find(m => m.id === "loggerhead")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />

            <FlatbackSection
              model={ContentData.find(m => m.id === "flatback")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />

            <HawksbillSection
              model={ContentData.find(m => m.id === "hawksbill")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />

            <OliveRidleySection
              model={ContentData.find(m => m.id === "olive-ridley")!}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={trackLaunchClick}
              launchUrl={launchUrl}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
            />
          </div>

          {/* Inside the Centre Section */}
          <InsideTheCentreSection />

          {/* Saving our Turtles Section */}
          <SavingTurtlesSection />

          {/* Knowledge Section */}
          <KnowledgeSection />

          {/* Butchulla Section */}
          <ButchullaSection
            model={ContentData.find(m => m.id === 'butchulla')!}
            onLaunchAR={handleLaunchAR}
            onTrackLaunchClick={trackLaunchClick}
            isChecking={xrIsChecking}
            isCompatible={xrIsCompatible}
          />

          {/* Butchulla Lores Section */}
          <ButchullaLoresSection />


          <ButchullaCountrySection />


          <CultureCommunitySection />


          <DaymanPointSection />

          {/* Footer */}
          <Footer />
        </div>
      )}

      {/* Splash - WebXR Onboarding */}
      <Fade visible={appState === AppState.SPLASH}>
        <Splash
          isLoading={isLoading}
          onClose={handleSplashClose}
          onBack={fromLandingRef.current ? handleBackToLanding : undefined}
          contentTitle={selectedModel ? `${selectedModel.displayName || ''} ${selectedModel.displaySubtitle || ''}`.trim() : undefined}
          autoStart={IS_EYEJACK_APP}
        />
      </Fade>

      {isOverlayMounted && (
        <div
          style={{
            opacity: isOverlayVisible ? 1 : 0,
            transition: `opacity ${OVERLAY_FADE_OUT_MS}ms ease-in-out`,
            pointerEvents: isOverlayVisible ? 'auto' : 'none',
          }}
        >
          <StartingAROverlay />
        </div>
      )}

      {/* AR Overlay - only when model is ready */}
      <Fade visible={appState === AppState.AR_EXPERIENCE && modelReady} duration={300}>
        <AROverlay
          onExit={handleExitAR}
          isRecording={isRecording}
          isPlaced={isPlaced}
        />
      </Fade>

      {/* Video Recorder UI - only when model is placed (not during placement) */}
      <Fade visible={appState === AppState.AR_EXPERIENCE && isPlaced} duration={300}>
        <VideoRecorderUI 
          buttonPosition="bottom-center"
          shrink
          shareTitle={selectedModel?.displayName || 'AR Experience'}
          onRecordingStart={() => setIsRecording(true)}
          onRecordingStop={() => setIsRecording(false)}
        />
      </Fade>

      {/* App Clip Exit Screen */}
      {appState === AppState.APP_CLIP_EXITED && (
        <AppClipExitScreen />
      )}
    </>
  );
}
